import React, { useState } from "react";
import { Container, Row, Col } from "react-bootstrap";
import IDSTable from "./IDSTable";
import Button from "react-bootstrap/Button";
import { reportsHeadCells } from "../util/headcells-util";
import XLSX from "sheetjs-style";
import Modal from "react-bootstrap/Modal";
import Form from "react-bootstrap/Form";
import { fetchReportData } from "../services/reports-services";
import moment from "moment";
import {
  auditLogFormatAction,
  transactionLogFormatAction,
} from "../util/common";

const PublicReports = () => {
  const reportsTableData = [
    {
      reportId: 1,
      report: "messagesPerDay",
      reportName: "Messages received by day",
      description: "The number of messages received by the tool each day",
    },
    {
      reportId: 2,
      report: "messagesPerWeek",
      reportName: "Messages received by week",
      description: "The number of messages received by the tool each week",
    },
    {
      reportId: 3,
      report: "messagesBySendingFacility",
      reportName: "Messages received by facility",
      description:
        "The number of messages received by the tool from each facility",
    },
    {
      reportId: 4,
      report: "errorQueueSummary",
      reportName: "Messages received in each queue",
      description: "The number of messages received by the tool in each queue",
    },
    {
      reportId: 5,
      report: "errorCodeByQueue",
      reportName:
        "Error types received in error queue and unknown facility queue",
      description:
        "The number of errors for each error type received by the tool in error queue and unknown facility queue",
    },
    {
      reportId: 6,
      report: "errorCodeByFacility",
      reportName: "Error types received for each facility",
      description:
        "The number of errors for each error type received by the tool by facility",
    },
    {
      reportId: 7,
      report: "messagesByStatusPerDay",
      reportName: "Messages received in master queue by status",
      description: "The number of messages received by the tool by status",
    },
    {
      reportId: 8,
      report: "auditLogPerDay",
      reportName: "Audit log report by day",
      description: "The number of audit logs created by day",
    },
    {
      reportId: 9,
      report: "auditLogPerWeek",
      reportName: "Audit log report by week",
      description: "The number of audit logs created by week",
    },
    {
      reportId: 10,
      report: "transactionLogPerDay",
      reportName: "Transaction log report by day",
      description: "The number of transaction logs created by day",
    },
    {
      reportId: 11,
      report: "transactionLogPerWeek",
      reportName: "Transaction log report by week",
      description: "The number of transaction logs created by week",
    },
  ];

  const [showDateSelectModal, setShowDateSelectModal] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [startDate, setStartDate] = useState(
    moment().startOf("month").format("YYYY-MM-DD")
  );
  const [endDate, setEndDate] = useState(moment().format("YYYY-MM-DD"));
  const [showDateRange, setShowDateRange] = useState("This Month");

  const handleDateSelectModalClose = () => setShowDateSelectModal(false);

  const handleSubmmit = (event) => {
    event.preventDefault();
    setShowDateSelectModal(false);
  };

  const fileName = () => {
    let filename = "";
    if (selectedRow.report === "messagesPerDay") {
      filename = "messages_per_day";
    } else if (selectedRow.report === "messagesPerWeek") {
      filename = "messages_per_week";
    } else if (selectedRow.report === "messagesBySendingFacility") {
      filename = "messages_by_facilities";
    } else if (selectedRow.report === "errorQueueSummary") {
      filename = "message_queue_summary";
    } else if (selectedRow.report === "errorCodeByQueue") {
      filename = "error_types_summary";
    } else if (selectedRow.report === "errorCodeByFacility") {
      filename = "error_codes_by_facility_summary";
    } else if (selectedRow.report === "messagesByStatusPerDay") {
      filename = "message_by_status_per_day";
    } else if (selectedRow.report === "auditLogPerDay") {
      filename = "audit_logs_per_day";
    } else if (selectedRow.report === "auditLogPerWeek") {
      filename = "audit_logs_per_week";
    } else if (selectedRow.report === "transactionLogPerDay") {
      filename = "transaction_logs_per_day";
    } else if (selectedRow.report === "transactionLogPerWeek") {
      filename = "transaction_logs_per_week";
    }
    return filename;
  };

  const excelHeaders = () => {
    let heading = [[]];
    if (selectedRow.report === "messagesPerDay") {
      heading = [["Date", "Messages Received", "Messages with errors"]];
    } else if (selectedRow.report === "messagesPerWeek") {
      heading = [
        ["Week Ending Date", "Messages Received", "Messages with errors"],
      ];
    } else if (selectedRow.report === "messagesBySendingFacility") {
      heading = [
        ["Facility Name", "Messages Received", "Messages with errors"],
      ];
    } else if (selectedRow.report === "errorQueueSummary") {
      heading = [
        [
          "Total messages in Master Queue",
          "Total messages in Error Queue",
          "Total messages in Unknown Facility Queue",
          "Total messages in Duplicate Queue",
          "Total messages in Discard Queue",
          "Total Error messages",
        ],
      ];
    } else if (selectedRow.report === "errorCodeByQueue") {
      heading = [["Error Code", "Error Code Name", "Error Code Count"]];
    } else if (selectedRow.report === "errorCodeByFacility") {
      heading = [
        [
          "Facility Identifier",
          "Facility Name",
          "Error Code",
          "Error Code Name",
          "Error Code Count",
        ],
      ];
    } else if (selectedRow.report === "messagesByStatusPerDay") {
      heading = [
        [
          "Date",
          "Messages Received",
          "Error Status",
          "Exception Status",
          "Processed Status",
          "Duplicate Status",
        ],
      ];
    } else if (selectedRow.report === "auditLogPerDay") {
      heading = [["Date", "Action Log"]];
    } else if (selectedRow.report === "auditLogPerWeek") {
      heading = [["Week Ending Date", "Action Log"]];
    } else if (selectedRow.report === "transactionLogPerDay") {
      heading = [["Date", "Log Description"]];
    } else if (selectedRow.report === "transactionLogPerWeek") {
      heading = [["Week Ending Date", "Log Description"]];
    }
    return heading;
  };

  const handleExport = async () => {
    let dataResponse = await fetchReportData(
      selectedRow.report,
      startDate,
      endDate
    );

    let sheetData = [];

    if (
      selectedRow.report === "messagesPerDay" ||
      selectedRow.report === "messagesPerWeek" ||
      selectedRow.report === "messagesBySendingFacility" ||
      selectedRow.report === "messagesByStatusPerDay"
    ) {
      sheetData = dataResponse;
    }

    if (selectedRow.report === "errorQueueSummary") {
      sheetData = [dataResponse];
    }

    if (selectedRow.report === "errorCodeByQueue") {
      sheetData = [
        ...dataResponse.errorQueue.errors,
        {
          errorCode: { v: "Total Count", s: { font: { bold: true } } },
          errorDisplayName: "",
          errorCodeCount: dataResponse.errorQueue.totalCount,
        },
      ];
    }

    if (selectedRow.report === "errorCodeByFacility") {
      const addedFacilities = new Set(); // Set to track added facilities

      dataResponse.sendingFacilities.forEach((facility) => {
        if (!addedFacilities.has(facility.sendingFacilityIdentifier)) {
          addedFacilities.add(facility.sendingFacilityIdentifier);

          let facilityTotalCount = 0; // Total error code count for the facility
          let isFirstRow = true; // Flag to track if it's the first row for the facility

          facility.errors.forEach((error) => {
            const rowData = [
              isFirstRow ? facility.sendingFacilityIdentifier : "", // Facility Identifier
              isFirstRow ? facility.sendingFacilityName : "", // Facility Name
              error.errorCode,
              error.errorDisplayName,
              error.errorCodeCount,
            ];

            sheetData.push(rowData);

            isFirstRow = false; // Update flag to false after the first row
            facilityTotalCount += error.errorCodeCount;
          });

          // Add facility total row
          sheetData.push([
            "",
            "",
            "",
            { v: "Total", s: { font: { bold: true } } },
            facilityTotalCount,
          ]);

          // Add an empty row after the total row
          sheetData.push([]);
        }
      });
    }

    if (
      selectedRow.report === "auditLogPerDay" ||
      selectedRow.report === "auditLogPerWeek"
    ) {
      dataResponse.forEach((day) => {
        let dayTotalRowAdded = false;

        if (day.userAuditLogList.length === 0) {
          const emptyRow = [day.dateString, "No Logs"];
          sheetData.push(emptyRow);

          // Add day total row after the empty log entry row
          const totalRow = [
            "",
            { v: "Total: " + day.total, s: { font: { bold: true } } },
          ];
          sheetData.push(totalRow);
          dayTotalRowAdded = true;
        } else {
          day.userAuditLogList.forEach((log, logIndex) => {
            const rowData = [
              logIndex === 0 ? day.dateString : "",
              auditLogFormatAction(log),
            ];
            sheetData.push(rowData);
          });

          // Add day total row after the logs for the day
          const totalRow = [
            "",
            { v: "Total: " + day.total, s: { font: { bold: true } } },
          ];
          sheetData.push(totalRow);
          dayTotalRowAdded = true; // Set flag to true after adding the total row
        }

        // If the day total row hasn't been added, add it after the empty log entry row
        if (!dayTotalRowAdded) {
          const totalRow = [
            "",
            { v: "Total: " + day.total, s: { font: { bold: true } } },
          ];
          sheetData.push(totalRow);
        }

        // Add an empty row after each total row
        sheetData.push([]);
      });
    }

    if (
      selectedRow.report === "transactionLogPerDay" ||
      selectedRow.report === "transactionLogPerWeek"
    ) {
      dataResponse.forEach((day) => {
        let dayTotalRowAdded = false;

        if (day.transactionAuditLogList.length === 0) {
          const emptyRow = [day.dateString, "No Logs"];
          sheetData.push(emptyRow);
          const totalRow = [
            "",
            { v: "Total: " + day.total, s: { font: { bold: true } } },
          ];
          sheetData.push(totalRow);
          dayTotalRowAdded = true;
        } else {
          // Add user log entries for the day
          day.transactionAuditLogList.forEach((log, logIndex) => {
            const rowData = [
              logIndex === 0 ? day.dateString : "",
              transactionLogFormatAction(log),
            ];
            sheetData.push(rowData);
          });

          // Add day total row after the logs for the day
          const totalRow = [
            "",
            { v: "Total: " + day.total, s: { font: { bold: true } } },
          ];
          sheetData.push(totalRow);
          dayTotalRowAdded = true;
        }

        // If the day total row hasn't been added, add it after the empty log entry row
        if (!dayTotalRowAdded) {
          const totalRow = [
            "",
            { v: "Total: " + day.total, s: { font: { bold: true } } },
          ];
          sheetData.push(totalRow);
        }

        // Add an empty row after each total row
        sheetData.push([]);
      });
    }

    //Create a new workbook and then add the header
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet([]);

    const headers = excelHeaders();

    // Add headers to the worksheet and apply bold style
    headers.forEach((headerRow, rowIndex) => {
      const headerRowArray = [];
      headerRow.forEach((header, colIndex) => {
        headerRowArray.push({ v: header, s: { font: { bold: true } } });
      });
      XLSX.utils.sheet_add_aoa(ws, [headerRowArray], {
        origin: { r: rowIndex, c: 0 },
      });
    });

    //Starting in the second row to avoid overriding and skipping headers
    XLSX.utils.sheet_add_json(ws, sheetData, {
      origin: "A2",
      skipHeader: true,
    });

    // Set width for all columns
    const columns = XLSX.utils.decode_range(ws["!ref"]).e.c + 1;
    ws["!cols"] = Array(columns).fill({ width: 30 });

    // Set alignment for all cells in the worksheet
    const alignment = { horizontal: "left", vertical: "center" };
    for (let r = 0; r <= XLSX.utils.decode_range(ws["!ref"]).e.r; ++r) {
      for (let c = 0; c <= columns; ++c) {
        const cellAddress = XLSX.utils.encode_cell({ r, c });
        ws[cellAddress] = ws[cellAddress] || {};
        ws[cellAddress].s = Object.assign({}, ws[cellAddress].s, { alignment });
      }
    }

    XLSX.utils.book_append_sheet(wb, ws, `Report ${startDate} - ${endDate}`);
    XLSX.writeFile(wb, fileName() + ".xlsx");
  };

  const handleSelectDateRange = () => {
    setShowDateSelectModal(true);
  };

  const emptyButtonsLayout = () => {
    return <div />;
  };

  const setSelectedRowCallback = (row) => {
    setSelectedRow(row);
  };

  const changeDateRange = () => {
    let startDateFormat = moment(startDate).format("D MMM YYYY");
    let endDateFormat = moment(endDate).format("D MMM YYYY");
    setShowDateRange(`${startDateFormat} - ${endDateFormat}`);
  };

  return (
    <>
      <Container fluid className="loincContainer">
        <Row style={{ marginLeft: "0px" }}>
          <Col xl={8}>
            <Row className="tableTitle generalFont">Public Reports</Row>
            <Row className="tableSubtitle"></Row>
          </Col>
        </Row>
        <IDSTable
          headCells={reportsHeadCells}
          rows={reportsTableData}
          addButtonName="Export to Excel"
          addButtonAction={handleExport}
          buttonsLayout={emptyButtonsLayout}
          setSelectedRowCallback={setSelectedRowCallback}
          handleShowFilter={handleSelectDateRange}
          isReportsTable={true}
          showDateRange={showDateRange}
        />
      </Container>
      <Modal
        show={showDateSelectModal}
        onHide={handleDateSelectModalClose}
        dialogClassName="custom-modal-width"
        centered
      >
        <Form onSubmit={handleSubmmit} noValidate>
          <Modal.Header className="modalHeaderContainer" closeButton>
            <Modal.Title className="modalHeader generalFont">
              Date Range
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className="modalBodyContainer">
            <Row>
              <Col>
                <Form.Group className="mb-3" controlId="form.startDate">
                  <React.Fragment>
                    <Form.Label className="modalLabel generalFont">
                      Start Date <i className="text-danger"></i>
                    </Form.Label>
                  </React.Fragment>
                  <Form.Control
                    className="modalInput generalFont"
                    type="date"
                    placeholder="Enter Text"
                    required
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row style={{ marginTop: "30px" }}>
              <Col>
                <Form.Group className="mb-3" controlId="form.endDate">
                  <React.Fragment>
                    <Form.Label className="modalLabel generalFont">
                      End Date <i className="text-danger"></i>
                    </Form.Label>
                  </React.Fragment>
                  <Form.Control
                    className="modalInput generalFont"
                    type="date"
                    placeholder="Enter Text"
                    required
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                  />
                </Form.Group>
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer className="modalFooterContainer">
            <Button
              type="submit"
              className="modalAddUpdateStyle generalFont flexCenter"
              onClick={changeDateRange}
            >
              Apply
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </>
  );
};

export default PublicReports;
